import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useStore } from '../../context/StoreContext';

const AccordionSection = ({ title, icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={icon} className="text-blue-600 text-sm" />
          </div>
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </div>
        <SafeIcon
          icon={FiIcons.FiChevronDown}
          className={`text-gray-400 text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 pl-16 text-sm text-gray-600 leading-relaxed">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HelpDrawer = ({ isOpen, onClose }) => {
  const { settings } = useStore();
  const scheduleDays = settings?.invoiceScheduleDays || [14, 5];
  const formattedDays = scheduleDays.sort((a, b) => b - a).join(' and ');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-[90]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[91] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiBookOpen} className="text-blue-700" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Admin Help Guide</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-500"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <AccordionSection
                title="Automated Payment Reminders"
                icon={FiIcons.FiMail}
                defaultOpen={true}
              >
                <p>
                  Reminder emails are automatically sent <strong>{formattedDays} days before the event</strong> to
                  customers with outstanding balances.
                </p>
                <p className="mt-2">
                  The system generates a unique Stripe payment link for each customer and includes it in the reminder.
                  Once the customer pays, the balance updates automatically via the Stripe webhook.
                </p>
                <p className="mt-2 text-gray-500 text-xs">
                  You can configure the reminder schedule in Settings under "Invoice Schedule Days".
                </p>
              </AccordionSection>

              <AccordionSection
                title="Cancellations vs. Refunds"
                icon={FiIcons.FiAlertTriangle}
              >
                <p>
                  Clicking <strong>"Cancel Booking"</strong> in the admin panel will instantly free the date/time slot
                  on the public calendar. However, <strong>any financial refunds must be processed manually in your
                  Stripe Dashboard</strong>.
                </p>
                <p className="mt-2">
                  The system does not issue automatic refunds. After cancelling a booking, navigate to your Stripe
                  Dashboard to process any partial or full refunds as appropriate.
                </p>
                <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  Always communicate with the customer before cancelling to avoid disputes.
                </div>
              </AccordionSection>

              <AccordionSection
                title="Manual Payments"
                icon={FiIcons.FiDollarSign}
              >
                <p>
                  For cash, check, or other offline payments, use the <strong>"Mark Balance as Paid"</strong> button
                  in the booking detail panel.
                </p>
                <p className="mt-2">
                  This will zero out the outstanding balance and queue the booking for access code delivery. This
                  action cannot be undone -- ensure you have received the payment before confirming.
                </p>
                <p className="mt-2 text-gray-500 text-xs">
                  The button only appears for confirmed bookings with an outstanding balance greater than $0.
                </p>
              </AccordionSection>

              <AccordionSection
                title="Blocking Dates for Maintenance"
                icon={FiIcons.FiTool}
              >
                <p>
                  To block out dates for venue maintenance, painting, or repairs without incurring charges,
                  create a booking using the <strong>"Offline Payment"</strong> option for a hidden event type
                  such as "Venue Maintenance".
                </p>
                <p className="mt-2">
                  This will block the calendar slot without generating a Stripe charge or requiring payment.
                  The time will appear as unavailable to customers on the public calendar.
                </p>
                <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                  <strong>Tip:</strong> Create an event type called "Venue Maintenance" and mark it as inactive
                  so it won't appear in the public booking form. You can still select it internally when
                  creating admin bookings.
                </div>
              </AccordionSection>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-400 text-center">
                Need more help? Contact your system administrator.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HelpDrawer;
